import {
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Feedback as FeedbackIcon,
  Logout as LogoutIcon,
  ManageAccounts as UserManagementIcon,
  QuestionAnswer as FAQIcon,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { getFlagPath, useLanguagePickerModel } from "@/components/common/languagePickerShared";
import { SidebarItem } from "@/components/layout/Sidebar/SidebarItem";
import { SidebarSection } from "@/components/layout/Sidebar/SidebarSection";
import { useAuth } from "@/contexts/AuthContext";
import { paths } from "@/routes/paths";
import { UserRole } from "@/utils/roleManager";

interface UtilitiesSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
  userHasPermission: (role: UserRole) => boolean;
  onLogoutClick?: () => void;
  isMobile?: boolean;
  onItemSelect?: () => void;
}

export const UtilitiesSection: React.FC<UtilitiesSectionProps> = ({
  isExpanded,
  onToggle,
  userHasPermission,
  onLogoutClick,
  isMobile = false,
  onItemSelect,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";
  const isSmallSidebarViewport = useMediaQuery(theme.breakpoints.down("lg"));
  const showsSidebarLanguagePicker = isMobile && isSmallSidebarViewport;
  const pointerRef = React.useRef<{ startY: number | null; moved: boolean }>({
    startY: null,
    moved: false,
  });

  const { user } = useAuth();
  const isAssociate = user?.role === UserRole.ASSOCIATE;
  const {
    availableLanguages,
    browserLocale,
    changeLanguage,
    currentLanguage,
    currentLanguageCode,
    getOptionLabel,
  } = useLanguagePickerModel();
  const [isLanguageExpanded, setIsLanguageExpanded] = React.useState(false);

  const handleLanguageToggle = (e?: React.MouseEvent) => {
    if (pointerRef.current.moved) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }

    setIsLanguageExpanded((prev) => !prev);
  };

  const handleLanguageChange = async (code: string) => {
    await changeLanguage(code);

    if (onItemSelect) {
      onItemSelect();
    }
  };

  const onPointerDown = (e: React.PointerEvent) => {
    pointerRef.current.startY = e.clientY;
    pointerRef.current.moved = false;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const startY = pointerRef.current.startY;
    if (startY != null && Math.abs(e.clientY - startY) > 6) {
      pointerRef.current.moved = true;
    }
  };

  const onPointerUp = () => {
    setTimeout(() => {
      pointerRef.current.startY = null;
    }, 0);
  };

  return (
    <SidebarSection
      title={t("nav.sections.utilities")}
      isExpanded={isExpanded}
      onToggle={onToggle}
      isMobile={isMobile}
    >
      {!isAssociate && (
        <SidebarItem
          label={t("settings.tabs.userManagement")}
          path={paths.utilities.userManagement}
          icon={<UserManagementIcon />}
          minimumRole={UserRole.STORE_ADMIN}
          userHasPermission={userHasPermission}
          isMobile={isMobile}
          afterSelect={onItemSelect}
        />
      )}

      <SidebarItem
        label={t("help.tabs.faq")}
        icon={<FAQIcon />}
        minimumRole={UserRole.CRAVE_ADMIN}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        path={paths.utilities.faq}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("help.tabs.feedback")}
        icon={<FeedbackIcon />}
        minimumRole={UserRole.NONE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        path={paths.utilities.feedback}
        afterSelect={onItemSelect}
      />
      {/* Profile is intentionally hidden until the account flow is functional. */}

      <SidebarItem
        label={t("common.signOut")}
        path="/logout"
        icon={<LogoutIcon />}
        onClick={onLogoutClick}
        minimumRole={UserRole.NONE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      {showsSidebarLanguagePicker && currentLanguage && (
        <>
          <Divider sx={{ my: 1, mx: 2 }} />
          <ListItem disablePadding sx={{ mb: 0.25 }}>
            <ListItemButton
              onClick={(e) => handleLanguageToggle(e)}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                overflow: "hidden",
                borderRadius: "12px",
                minHeight: "40px",
                py: 0.625,
                px: 1.5,
                mx: 0.5,
                color: "primary.main",
                transition: "all 0.3s ease-in-out",
                boxShadow: "inset 0px 0px 0px 1px transparent",
                "&:hover": {
                  background: isRtl
                    ? "/* @noflip */ linear-gradient(225deg, rgba(79, 70, 229, 0.08) 0%, rgba(0, 102, 255, 0.08) 100%)"
                    : "/* @noflip */ linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(0, 102, 255, 0.08) 100%)",
                  transform: "translateX(2px)",
                  boxShadow:
                    "inset 0px 0px 0px 1px rgba(79, 70, 229, 0.15), 0 4px 12px rgba(79, 70, 229, 0.12)",
                  border: 0,
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 28,
                  justifyContent: "center",
                  color: "inherit",
                  marginInlineStart: 0,
                  marginInlineEnd: theme.spacing(1.5),
                }}
              >
                <Box
                  component="img"
                  src={getFlagPath(currentLanguage, browserLocale)}
                  alt={t("common.languagePicker.text.flag", {
                    optionDisplay: currentLanguage.display,
                  })}
                  sx={{
                    width: 20,
                    height: 14,
                    display: "block",
                    borderRadius: "3px",
                    border: "1px solid rgba(15, 23, 42, 0.12)",
                    objectFit: "cover",
                    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.12)",
                  }}
                />
              </ListItemIcon>
              <ListItemText
                sx={{
                  flex: "1 1 auto",
                  m: 0,
                  "& .MuiListItemText-primary": {
                    width: "100%",
                  },
                }}
                primary={
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 400,
                      color: "inherit",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("common.language")}
                  </Typography>
                }
                secondary={
                  <Typography
                    sx={{
                      fontSize: "0.625rem",
                      lineHeight: 1.2,
                      color: "text.secondary",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getOptionLabel(currentLanguage)}
                  </Typography>
                }
              />
              <Box
                sx={{
                  p: 0.5,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "rgba(79, 70, 229, 0.8)",
                  transition: "transform 0.2s ease",
                  transform: isLanguageExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  marginInlineStart: theme.spacing(0.5),
                  marginInlineEnd: 0,
                  flexShrink: 0,
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </Box>
            </ListItemButton>
          </ListItem>

          <Collapse in={isLanguageExpanded} timeout="auto">
            <List
              disablePadding
              sx={{
                mt: 1,
                marginInlineStart: 2.5,
                paddingInlineStart: 1,
                mb: 0.75,
              }}
            >
              {availableLanguages.map((option) => {
                const isSelected = option.code === currentLanguageCode;

                return (
                  <ListItem key={option.code} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => void handleLanguageChange(option.code)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minHeight: "40px",
                        borderRadius: "12px",
                        py: 0.625,
                        px: 1.5,
                        mx: 0.5,
                        color: "primary.main",
                        transition: "all 0.3s ease-in-out",
                        boxShadow: "inset 0px 0px 0px 1px transparent",
                        "&:hover": {
                          background: isRtl
                            ? "/* @noflip */ linear-gradient(225deg, rgba(79, 70, 229, 0.08) 0%, rgba(0, 102, 255, 0.08) 100%)"
                            : "/* @noflip */ linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(0, 102, 255, 0.08) 100%)",
                          transform: "translateX(2px)",
                          boxShadow:
                            "inset 0px 0px 0px 1px rgba(79, 70, 229, 0.15), 0 4px 12px rgba(79, 70, 229, 0.12)",
                          border: 0,
                        },
                        "&.Mui-selected": {
                          background: isRtl
                            ? "/* @noflip */ linear-gradient(256deg, #06F 0%, #00B7FF 100%)"
                            : "/* @noflip */ linear-gradient(104deg, #06F 0%, #00B7FF 100%)",
                          color: "#fff",
                          boxShadow:
                            "inset 0px 0px 0px 1px rgba(79, 70, 229, 0.15), 0 6px 16px rgba(0, 102, 255, 0.25)",
                          border: 0,
                        },
                        "&.Mui-selected:hover": {
                          background: isRtl
                            ? "/* @noflip */ linear-gradient(256deg, #06F 0%, #00B7FF 100%)"
                            : "/* @noflip */ linear-gradient(104deg, #06F 0%, #00B7FF 100%)",
                          color: "#fff",
                          boxShadow:
                            "inset 0px 0px 0px 1px rgba(79, 70, 229, 0.15), 0 6px 16px rgba(0, 102, 255, 0.25)",
                          border: 0,
                          transform: "translateX(2px)",
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={getFlagPath(option, browserLocale)}
                        alt={t("common.languagePicker.text.flag", {
                          optionDisplay: option.display,
                        })}
                        sx={{
                          width: 20,
                          height: 14,
                          display: "block",
                          borderRadius: "3px",
                          border: "1px solid rgba(15, 23, 42, 0.12)",
                          objectFit: "cover",
                          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.12)",
                          marginInlineEnd: theme.spacing(1.25),
                          flexShrink: 0,
                        }}
                      />
                      <ListItemText
                        primary={
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: isSelected ? 600 : 400,
                              color: "inherit",
                            }}
                          >
                            {getOptionLabel(option)}
                          </Typography>
                        }
                      />
                      {isSelected && (
                        <Box
                          sx={{
                            p: 0.5,
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginInlineStart: 0.5,
                            marginInlineEnd: 0.5,
                            flexShrink: 0,
                          }}
                        >
                          <CheckIcon fontSize="small" sx={{ color: "inherit" }} />
                        </Box>
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        </>
      )}
    </SidebarSection>
  );
};
